import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Activity, ShieldCheck, ShieldAlert, User } from "lucide-react";

const ENTITY_LABELS = {
    settings: "Site settings",
    product: "Product",
    editor: "Editor",
    employee: "Employee",
    designation: "Designation",
    distributor: "Distributor",
    invoice_template: "Invoice template",
    invoice: "Invoice",
    payment: "Payment",
    journal: "Journal entry",
    customer: "Customer (master)",
    external_invoice: "External invoice",
};

const ACTION_DOT = {
    create: "bg-sky-600",
    update: "bg-amber-500",
    delete: "bg-red-500",
    cancel: "bg-orange-500",
    reset_password: "bg-purple-600",
    approve: "bg-emerald-600",
};

const ACTOR_ICON = {
    admin: <ShieldCheck className="w-3.5 h-3.5 text-sky-700" />,
    editor: <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />,
    employee: <User className="w-3.5 h-3.5 text-stone-600" />,
};

const today = () => new Date().toISOString().slice(0, 10);

export default function AdminActivityLog() {
    const [items, setItems] = useState([]);
    const [entity, setEntity] = useState("all");
    const [actorRole, setActorRole] = useState("all");
    const [actorEmail, setActorEmail] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (entity !== "all") params.entity = entity;
            if (actorRole !== "all") params.actor_role = actorRole;
            if (actorEmail.trim()) params.actor_email = actorEmail.trim();
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            const { data } = await api.get("/admin/activity", { params });
            setItems(data);
        } finally { setLoading(false); }
    };

    // Debounce email search by 350ms; refetch immediately when other filters change
    useEffect(() => {
        const id = setTimeout(load, actorEmail ? 350 : 0);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [entity, actorRole, actorEmail, fromDate, toDate]);

    const clearFilters = () => {
        setEntity("all"); setActorRole("all"); setActorEmail("");
        setFromDate(""); setToDate("");
    };

    return (
        <div data-testid="admin-activity">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Activity className="w-3 h-3" />Audit trail</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Activity log</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-xl">
                        Every create, update, delete, cancel and approval — by admins, editors and Accounts & Billing staff — appears here in chronological order. Filter by who, what or when to find exactly what changed.
                    </p>
                </div>
            </div>

            <div className="bg-white border border-sky-100 p-4 mb-4 grid lg:grid-cols-6 sm:grid-cols-2 gap-3 items-end">
                <div className="lg:col-span-1">
                    <div className="label-eyebrow text-stone-500 mb-1">Entity</div>
                    <Select value={entity} onValueChange={setEntity}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="filter-entity"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All entities</SelectItem>
                            {Object.entries(ENTITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="lg:col-span-1">
                    <div className="label-eyebrow text-stone-500 mb-1">Actor role</div>
                    <Select value={actorRole} onValueChange={setActorRole}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="filter-actor"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Anyone</SelectItem>
                            <SelectItem value="admin">Admins</SelectItem>
                            <SelectItem value="editor">Editors</SelectItem>
                            <SelectItem value="employee">Employees</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="lg:col-span-2">
                    <div className="label-eyebrow text-stone-500 mb-1">Actor email contains</div>
                    <Input value={actorEmail} onChange={(e) => setActorEmail(e.target.value)} placeholder="e.g. stock_acct@" className="rounded-none border-stone-300" data-testid="filter-email" />
                </div>
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">From date</div>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate || today()} className="rounded-none border-stone-300" data-testid="filter-from" />
                </div>
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">To date</div>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate || undefined} max={today()} className="rounded-none border-stone-300" data-testid="filter-to" />
                </div>
                <div className="sm:col-span-2 lg:col-span-6 flex justify-between items-center pt-1">
                    <div className="text-xs text-stone-500">{items.length} {items.length === 1 ? "entry" : "entries"}</div>
                    {(entity !== "all" || actorRole !== "all" || actorEmail || fromDate || toDate) && (
                        <button onClick={clearFilters} className="text-xs text-sky-700 hover:underline" data-testid="filter-clear">Clear all filters</button>
                    )}
                </div>
            </div>

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500 whitespace-nowrap">When</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Actor</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Action</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Entity</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Summary</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="p-6 text-center text-stone-500">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={5} className="p-10 text-center text-stone-500">No activity matches the current filters.</td></tr>
                        ) : items.map((row) => (
                            <tr key={row.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`activity-row-${row.id}`}>
                                <td className="p-3 text-stone-500 whitespace-nowrap font-mono text-xs">
                                    {new Date(row.created_at).toLocaleString("en-IN", { hour12: false })}
                                </td>
                                <td className="p-3">
                                    <div className="flex items-center gap-2">
                                        {ACTOR_ICON[row.actor_role] || <User className="w-3.5 h-3.5 text-stone-400" />}
                                        <span className="font-medium text-slate-900 text-xs" data-testid={`activity-actor-${row.id}`}>{row.actor_email || "—"}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-stone-400">{row.actor_role || "—"}</span>
                                    </div>
                                </td>
                                <td className="p-3 whitespace-nowrap">
                                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${ACTION_DOT[row.action] || "bg-stone-400"}`} />
                                    <span className="capitalize text-xs">{(row.action || "").replace(/_/g, " ")}</span>
                                </td>
                                <td className="p-3 text-xs text-stone-600 whitespace-nowrap">{ENTITY_LABELS[row.entity] || row.entity || "—"}</td>
                                <td className="p-3 text-stone-700">{row.summary}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
